
// ============================================================================
// 2. UPDATED SCHEMA BUILDER - Fixed palette, improved layout
// ============================================================================
import { useState, useCallback, useEffect } from "react";
import { Button, Empty, message, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent, DragStartEvent, DragOverlay, DragOverEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SchemaNode, SchemaFieldType, schemaNodeToJsonSchema } from "../../types/appointments/SchemaBuilderTypes";
import ComponentPalette from "./ComponentPalette";
import SchemaNodeComponent from "./SchemaNodeComponent";
import SchemaPreviewModal from "./SchemaPreviewModal";

// Finds which parent (if any) contains a node with the given id, returns { parentId, siblingId }
const findParentInfo = (nodeList: SchemaNode[], targetId: string): { parentId: string; siblingId: string } | null => {
	for (const node of nodeList) {
		if (node.children) {
			if (node.children.some(c => c.id === targetId)) return { parentId: node.id, siblingId: targetId };
			const result = findParentInfo(node.children, targetId);
			if (result) return result;
		}
	}
	return null;
};

export const SchemaBuilder: React.FC<{
	initialSchema?: SchemaNode[];
	onChange: (nodes: SchemaNode[], jsonSchema: any) => void;
}> = ({ initialSchema = [], onChange }) => {
	const [nodes, setNodes] = useState<SchemaNode[]>(initialSchema);
	const [activeId, setActiveId] = useState<string | null>(null);
	const [activePaletteItem, setActivePaletteItem] = useState<{ label: string; icon: React.ReactNode } | null>(null);
	const [dragOverNodeId, setDragOverNodeId] = useState<string | null>(null);
	const [previewVisible, setPreviewVisible] = useState(false);

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

	// Sync internal nodes state with initialSchema prop changes
	useEffect(() => {
		setNodes(initialSchema);
	}, [initialSchema]);

	const updateNodes = useCallback((newNodes: SchemaNode[]) => {
		setNodes(newNodes);
		const jsonSchema = schemaNodeToJsonSchema(newNodes);
		onChange(newNodes, jsonSchema);
	}, [onChange]);

	const makeNewNode = (type: SchemaFieldType, parentId?: string): SchemaNode => {
		const node: SchemaNode = {
			id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
			type,
			key: `${type}_${Date.now()}`,
			label: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
			order: 0,
			parentId,
			children: ["section", "topic", "object", "array"].includes(type) ? [] : undefined,
			required: false,
		};
		if (type === "enum") node.enumConfig = { options: ["Option 1", "Option 2"] };
		if (type === "array") node.arrayConfig = { itemType: "text" };
		return node;
	};

	// Insert a new node after a specific sibling (or append if no insertAfterId)
	const insertNode = (type: SchemaFieldType, insertAfterId?: string, parentId?: string) => {
		const newNode = makeNewNode(type, parentId);

		if (parentId) {
			const addInParent = (nodeList: SchemaNode[]): SchemaNode[] =>
				nodeList.map(node => {
					if (node.id === parentId) {
						const children = [...(node.children || [])];
						if (insertAfterId) {
							const idx = children.findIndex(c => c.id === insertAfterId);
							children.splice(idx !== -1 ? idx + 1 : children.length, 0, newNode);
						} else {
							children.push(newNode);
						}
						return { ...node, children: children.map((c, i) => ({ ...c, order: i })) };
					}
					if (node.children) return { ...node, children: addInParent(node.children) };
					return node;
				});
			updateNodes(addInParent(nodes));
		} else {
			if (insertAfterId) {
				const idx = nodes.findIndex(n => n.id === insertAfterId);
				const updated = [...nodes];
				updated.splice(idx !== -1 ? idx + 1 : updated.length, 0, newNode);
				updateNodes(updated.map((n, i) => ({ ...n, order: i })));
			} else {
				updateNodes([...nodes, newNode]);
			}
		}
		message.success(`Added ${type}`);
	};

	// Legacy handler used by SchemaNodeComponent's "add child" quick-add
	const handleAddComponent = (type: SchemaFieldType, parentId?: string) => {
		insertNode(type, undefined, parentId);
	};

	const handleUpdateNode = (nodeId: string, updates: Partial<SchemaNode>) => {
		const updateNodeRecursive = (nodes: SchemaNode[]): SchemaNode[] => {
			return nodes.map(node => {
				if (node.id === nodeId) return { ...node, ...updates };
				if (node.children) return { ...node, children: updateNodeRecursive(node.children) };
				return node;
			});
		};
		updateNodes(updateNodeRecursive(nodes));
	};

	const handleDeleteNode = (nodeId: string) => {
		const deleteNodeRecursive = (nodes: SchemaNode[]): SchemaNode[] => {
			return nodes
				.filter(node => node.id !== nodeId)
				.map(node => ({
					...node,
					children: node.children ? deleteNodeRecursive(node.children) : undefined
				}));
		};
		updateNodes(deleteNodeRecursive(nodes));
		message.success("Deleted component");
	};

	const handleDuplicateNode = (nodeId: string) => {
		const findAndDuplicate = (nodes: SchemaNode[], targetId: string): SchemaNode[] | null => {
			for (let i = 0; i < nodes.length; i++) {
				if (nodes[i].id === targetId) {
					const duplicated: SchemaNode = {
						...nodes[i],
						id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
						key: `${nodes[i].key}_copy`,
						label: `${nodes[i].label} (Copy)`,
						children: nodes[i].children ? JSON.parse(JSON.stringify(nodes[i].children)) : undefined
					};
					return [...nodes.slice(0, i + 1), duplicated, ...nodes.slice(i + 1)];
				}
				if (nodes[i].children) {
					const result = findAndDuplicate(nodes[i].children!, targetId);
					if (result) return nodes.map(n => (n.id === nodes[i].id ? { ...n, children: result } : n));
				}
			}
			return null;
		};

		const result = findAndDuplicate(nodes, nodeId);
		if (result) {
			updateNodes(result);
			message.success("Duplicated component");
		}
	};

	const handleDragOver = (event: DragOverEvent) => {
		const { active, over } = event;
		if (active.data.current?.fromPalette) {
			setDragOverNodeId(over ? (over.id as string) : null);
		}
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setActiveId(null);
		setActivePaletteItem(null);
		setDragOverNodeId(null);

		if (!over) return;

		// Handle palette item drop — insert after the hovered node
		if (active.data.current?.fromPalette) {
			const type = active.data.current.type as SchemaFieldType;
			const overId = over.id as string;
			// Is it a top-level node?
			const rootIndex = nodes.findIndex(n => n.id === overId);
			if (rootIndex !== -1) {
				insertNode(type, overId);
				return;
			}
			// Is it a child node?
			const parentInfo = findParentInfo(nodes, overId);
			if (parentInfo) {
				insertNode(type, parentInfo.siblingId, parentInfo.parentId);
				return;
			}
			// Fallback: append at root
			insertNode(type);
			return;
		}

		// Handle reordering
		if (active.id === over.id) return;

		const findNodeById = (nodes: SchemaNode[], id: string): SchemaNode | null => {
			for (const node of nodes) {
				if (node.id === id) return node;
				if (node.children) {
					const found = findNodeById(node.children, id);
					if (found) return found;
				}
			}
			return null;
		};

		const activeNode = findNodeById(nodes, active.id as string);
		const overNode = findNodeById(nodes, over.id as string);

		if (!activeNode || !overNode) return;

		if (activeNode.parentId !== overNode.parentId) {
			message.warning("Can only reorder items within the same container");
			return;
		}

		const reorderNodes = (nodes: SchemaNode[]): SchemaNode[] => {
			if (activeNode.parentId) {
				return nodes.map(node => {
					if (node.children?.some(c => c.id === active.id)) {
						const oldIndex = node.children.findIndex(c => c.id === active.id);
						const newIndex = node.children.findIndex(c => c.id === over.id);
						const reordered = [...node.children];
						const [removed] = reordered.splice(oldIndex, 1);
						reordered.splice(newIndex, 0, removed);
						return { ...node, children: reordered.map((c, idx) => ({ ...c, order: idx })) };
					}
					if (node.children) return { ...node, children: reorderNodes(node.children) };
					return node;
				});
			} else {
				const oldIndex = nodes.findIndex(n => n.id === active.id);
				const newIndex = nodes.findIndex(n => n.id === over.id);
				const reordered = [...nodes];
				const [removed] = reordered.splice(oldIndex, 1);
				reordered.splice(newIndex, 0, removed);
				return reordered.map((n, idx) => ({ ...n, order: idx }));
			}
		};

		updateNodes(reorderNodes(nodes));
	};

	const handleDragStart = (event: DragStartEvent) => {
		const data = event.active.data.current;
		if (data?.fromPalette) {
			setActivePaletteItem({ label: data.label, icon: data.icon });
		} else {
			setActiveId(event.active.id as string);
		}
		setDragOverNodeId(null);
	};

	const findNodeById = (nodes: SchemaNode[], id: string): SchemaNode | null => {
		for (const node of nodes) {
			if (node.id === id) return node;
			if (node.children) {
				const found = findNodeById(node.children, id);
				if (found) return found;
			}
		}
		return null;
	};

	const activeNode = activeId ? findNodeById(nodes, activeId) : null;

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragOver={handleDragOver}
		>
			<div className="schema-builder flex gap-3 h-full">
				{/* Component Palette */}
				<div className="w-44 flex-shrink-0 border-r border-indigo-100 pr-3">
					<ComponentPalette onAddComponent={handleAddComponent} />
				</div>

				{/* Canvas */}
				<div className="flex-1 overflow-y-auto">
					<div className="mb-2 flex justify-between items-center">
						<Typography.Text type="secondary" className="text-xs">
							{nodes.length === 0 ? "Add components from the left panel" : `${nodes.length} top-level component${nodes.length !== 1 ? "s" : ""}`}
						</Typography.Text>
						<Button
							size="small"
							type="text"
							icon={<EyeOutlined />}
							onClick={() => setPreviewVisible(true)}
							className="text-indigo-500 hover:text-indigo-600"
						>
							Preview
						</Button>
					</div>

					{nodes.length === 0 ? (
						<Empty
							description={<Typography.Text type="secondary" className="text-xs">No components yet</Typography.Text>}
							className="mt-8"
						/>
					) : (
						<SortableContext items={nodes.map(n => n.id)} strategy={verticalListSortingStrategy}>
							<div className="space-y-1">
								{nodes.map(node => (
									<SchemaNodeComponent
										key={node.id}
										node={node}
										onUpdate={handleUpdateNode}
										onDelete={handleDeleteNode}
										onDuplicate={handleDuplicateNode}
										onAddChild={handleAddComponent}
										level={0}
										isDragOver={dragOverNodeId === node.id}
									/>
								))}
							</div>
						</SortableContext>
					)}
				</div>

				<DragOverlay dropAnimation={null}>
					{activePaletteItem ? (
						<div className="flex items-center gap-2 bg-white border border-blue-300 rounded px-3 py-1.5 shadow-lg" style={{ pointerEvents: "none" }}>
							<span className="text-gray-500 text-sm">{activePaletteItem.icon}</span>
							<Typography.Text className="text-xs font-medium">{activePaletteItem.label}</Typography.Text>
						</div>
					) : activeNode ? (
						<div className="bg-white border border-gray-200 rounded px-3 py-1.5 shadow-md" style={{ pointerEvents: "none" }}>
							<Typography.Text className="text-xs">{activeNode.label}</Typography.Text>
						</div>
					) : null}
				</DragOverlay>
			</div>

			<SchemaPreviewModal
				visible={previewVisible}
				schema={schemaNodeToJsonSchema(nodes)}
				onClose={() => setPreviewVisible(false)}
			/>
		</DndContext>
	);
};

export default SchemaBuilder;